import type { UseFormReturn } from 'react-hook-form';

import { useMemo, useRef } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { EditorRefHandle } from '~/components';

import { useClients } from '~/api/clients';
import { FileDropzone, RichTextEditor, Title, UploadedFile } from '~/components';
import { Field } from '~/components/Form';
import { mergeRefs } from '~/utils/mergeRefs';

import type { CreateEditQuotationFormType } from '../types';

import { useAttachment, useMailInContactLanguage } from '../hooks';
import { columnsStyle } from './QuotationClientMessageSection.css';

type Props = {
  form: UseFormReturn<CreateEditQuotationFormType>;
  quotationId: number;
};

export const QuotationClientMessageSection = ({ form, quotationId }: Props) => {
  const { data: clients } = useClients();
  const { control, setValue, watch } = form;

  const { t } = useTranslation(['quotations', 'validation']);

  const attachment = useAttachment(quotationId);
  const mailEditorRef = useRef<EditorRefHandle>(null);

  const watchContactId = watch('contactId');
  const selectedContact = useMemo(
    () => clients.flatMap(({ contacts }) => contacts).find((contact) => contact.id === watchContactId) ?? null,
    [clients, watchContactId],
  );

  useMailInContactLanguage(selectedContact, (mail) => {
    setValue('mail', mail);
    mailEditorRef.current?.setContent(mail);
  });

  return (
    <>
      <Title as="h2" styleAs="h3">
        {t('quotations:createEdit.attachments.title')}
      </Title>

      <div className={columnsStyle}>
        {/* Mail */}
        <Controller
          control={control}
          name="mail"
          render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
            <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
              <Field.Label>{t('quotations:fields.mail.label')}</Field.Label>

              <RichTextEditor
                isInvalid={invalid}
                mode="html"
                onBlur={onBlur}
                onChange={onChange}
                ref={mergeRefs(ref, mailEditorRef)}
                shortcodes={[
                  { label: t('quotations:richText.shortcodes.Clientname'), code: '#CLIENTNAME#' },
                  { label: t('quotations:richText.shortcodes.Quotationlink'), code: '#QUOTATIONLINK#' },
                  { label: t('quotations:richText.shortcodes.Freelancerfirstname'), code: '#FREELANCERFIRSTNAME#' },
                ]}
                value={value}
              />

              <Field.Error match={!!error}>{error?.message}</Field.Error>
            </Field.Root>
          )}
          rules={{
            required: { value: true, message: t('validation:required') },
            validate: { notEmpty: () => !mailEditorRef.current?.isEmpty() || t('validation:required') },
          }}
        />

        {/* Attachment */}
        <Field.Root>
          <Field.Label optional>{t('quotations:fields.attachment.label')}</Field.Label>

          {attachment.file ? (
            <UploadedFile
              error={attachment.file.error}
              fileSize={attachment.file.size}
              name={attachment.file.name}
              onRemove={attachment.remove}
              onRetry={attachment.retry}
              onView={attachment.open}
              // The endpoint does not stream progress, so the upload can only be shown as indeterminate.
              progress={attachment.file.isUploading ? null : undefined}
              removing={attachment.isRemoving}
            />
          ) : (
            <FileDropzone accept={['.pdf']} maxFileSizeInMegaByte={8} onSelect={attachment.select} />
          )}
        </Field.Root>
      </div>
    </>
  );
};
